/**
 * MorningClockPrompt - Bottom sheet overlay prompting employees to clock in.
 *
 * Shown when ALL conditions are true:
 * 1. Weekday (Mon-Fri in Pacific timezone)
 * 2. 5 AM - 11 AM Pacific
 * 3. No time_records entry for today (useMyTimesheet returns null)
 * 4. Not dismissed this session (sessionStorage key: clockPromptDismissed)
 *
 * @example
 * <MorningClockPrompt employeeName="Carlos" onClockIn={() => refetch()} />
 */

import { useState, useCallback, useEffect } from 'react';
import { useMyTimesheet, useClockIn } from '../../../hooks/useTimesheets';
import { captureGpsOrNull } from '../utils/captureGpsOrNull';

interface MorningClockPromptProps {
  /** Employee first name for the greeting */
  employeeName: string;
  /** Optional callback fired after successful clock-in */
  onClockIn?: () => void;
}

const SESSION_KEY = 'twg:time:clockPromptDismissed';

/** Get today's date in Pacific timezone (YYYY-MM-DD) */
function getPacificDate(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

/** Get current hour in Pacific timezone (0-23) */
function getPacificHour(): number {
  const timeStr = new Date().toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour12: false,
    hour: 'numeric',
  });
  return parseInt(timeStr, 10);
}

/** Get current day of week in Pacific timezone (0=Sun, 6=Sat) */
function getPacificDayOfWeek(): number {
  const fullDay = new Date().toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'long',
  });
  const dayMap: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  return dayMap[fullDay] ?? 0;
}

/** Check if the prompt was already dismissed this session */
function isDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Mark the prompt as dismissed for this session */
function dismissForSession(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, 'true');
  } catch {
    // sessionStorage unavailable — degrade gracefully
  }
}

export function MorningClockPrompt({ employeeName, onClockIn }: MorningClockPromptProps) {
  const todayDate = getPacificDate();
  const { data: timesheet, isLoading: timesheetLoading } = useMyTimesheet(todayDate);
  const clockIn = useClockIn();

  const [dismissed, setDismissed] = useState(isDismissedThisSession);
  const [capturingGps, setCapturingGps] = useState(false);
  const [windowClosed, setWindowClosed] = useState(false);

  // Auto-hide when the 5-11 AM window closes
  useEffect(() => {
    const msUntil11 = (() => {
      const hour = getPacificHour();
      if (hour >= 11) return 0;
      return (11 - hour) * 3600000; // Rough — checks every hour
    })();
    if (msUntil11 <= 0) { setWindowClosed(true); return; }
    const timer = setTimeout(() => setWindowClosed(true), msUntil11);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = useCallback(() => {
    dismissForSession();
    setDismissed(true);
  }, []);

  const handleClockIn = useCallback(async () => {
    setCapturingGps(true);
    const gps = await captureGpsOrNull();
    setCapturingGps(false);
    clockIn.mutate(gps ?? undefined, {
      onSuccess: () => {
        handleDismiss();
        onClockIn?.();
      },
    });
  }, [clockIn, handleDismiss, onClockIn]);

  // --- Gate conditions ---

  // Don't render until query resolves (avoid flash)
  if (timesheetLoading) return null;

  // Already has a record for today
  if (timesheet !== null) return null;

  // Already dismissed this session or window closed
  if (dismissed || windowClosed) return null;

  // Check time window: Mon-Fri 5-11 AM Pacific
  const day = getPacificDayOfWeek();
  if (day === 0 || day === 6) return null;

  const hour = getPacificHour();
  if (hour < 5 || hour >= 11) return null;

  const isLoading = capturingGps || clockIn.isPending;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
      onClick={handleDismiss}
      role="presentation"
    >
      <div
        className="bg-white w-full rounded-t-2xl px-6 py-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Clock in prompt"
      >
        {/* Greeting */}
        <h2 className="text-xl font-display font-bold text-forest-900 mb-1">
          Good morning, {employeeName}!
        </h2>
        <p className="text-sage-600 mb-6">Ready to start your day?</p>

        {/* Primary action */}
        <button
          onClick={handleClockIn}
          disabled={isLoading}
          className="w-full py-3.5 rounded-xl bg-forest-700 hover:bg-forest-800 text-white font-semibold text-lg flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-60"
        >
          {isLoading ? (
            'Capturing location...'
          ) : (
            <>
              {/* GPS pin icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              Clock In Now
            </>
          )}
        </button>

        {/* Error message */}
        {clockIn.isError && (
          <p className="text-red-600 text-sm mt-2 text-center">
            {(clockIn.error as Error).message}
          </p>
        )}

        {/* Dismiss link */}
        <button
          onClick={handleDismiss}
          className="w-full mt-4 text-center text-sage-500 text-sm hover:text-sage-700 transition-colors"
        >
          Not yet — remind me later
        </button>
      </div>
    </div>
  );
}
